import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { clearSettingsSessionId, setSettingsSessionId } from '@gonfalon/analytics/settingsSessionContext';
import { PROJECT_SETTINGS_DRAWER_PARAM } from '@gonfalon/navigator/navigators';

import { isFlattenedSettingsEnabled } from './isFlattenedSettingsEnabled';

const trackSettings = createTrackerForCategory('Settings');

export type SettingsEntrySource = 'gear' | 'gear-menu' | 'project-switcher';
export type SettingsTask = 'project-renamed' | 'project-tags-updated' | 'project-deleted';
export type SettingsNavigationSource = 'rail' | 'search-page' | 'search-heading' | 'breadcrumb' | 'drawer';

const ENTRY_WINDOW_MS = 5000;
const NAVIGATION_WINDOW_MS = 5000;
const DRAWER_PAGE_SUFFIX = '?projectSettings';

type SettingsSession = {
  id: number;
  startedAt: number;
  entry: SettingsEntrySource | 'link';
  landingPage: string;
  currentPage: string;
  currentPageKey: string;
  pageCount: number;
  reachedDestination: boolean;
};

let session: SettingsSession | undefined;
let nextSessionId = 1;
let pendingEntry: { source: SettingsEntrySource; at: number } | undefined;
let pendingNavigation: { source: SettingsNavigationSource; at: number } | undefined;

export function markSettingsEntry(source: SettingsEntrySource) {
  pendingEntry = { source, at: performance.now() };
}

export function markSettingsNavigation(source: SettingsNavigationSource) {
  pendingNavigation = { source, at: performance.now() };
}

function takeEntry(now: number) {
  const entry = pendingEntry && now - pendingEntry.at <= ENTRY_WINDOW_MS ? pendingEntry.source : 'link';
  pendingEntry = undefined;
  return entry;
}

function takeNavigation(now: number) {
  const source =
    pendingNavigation && now - pendingNavigation.at <= NAVIGATION_WINDOW_MS ? pendingNavigation.source : 'other';
  pendingNavigation = undefined;
  return source;
}

export function isSettingsPathPattern(pattern: string) {
  return (
    pattern === '/settings' ||
    pattern.startsWith('/settings/') ||
    /^\/projects\/:projectKey\/settings(\/|$)/.test(pattern)
  );
}

function settingsScope(pattern: string) {
  if (pattern.includes(':envKey') || pattern.startsWith('/settings/sdk-keys')) {
    return 'environment';
  }
  return pattern.startsWith('/projects/') ? 'project' : 'organization';
}

function endSession(exitTo: string) {
  if (!session) {
    return;
  }
  const ended = session;
  session = undefined;
  setSettingsSessionId(ended.id);
  const durationMs = Math.round(performance.now() - ended.startedAt);
  trackSettings('Settings Session Ended', {
    entry: ended.entry,
    landingPage: ended.landingPage,
    lastPage: ended.currentPage,
    pageCount: ended.pageCount,
    reachedDestination: ended.reachedDestination,
    durationMs,
    exitTo,
    flattened: isFlattenedSettingsEnabled(),
    metricValue: ended.pageCount,
  });
  if (ended.entry !== 'link' && !ended.reachedDestination) {
    trackSettings('Settings Session Abandoned', {
      entry: ended.entry,
      landingPage: ended.landingPage,
      durationMs,
      exitTo,
      flattened: isFlattenedSettingsEnabled(),
    });
  }
  clearSettingsSessionId();
}

function reachDestination(page: string, arrivedVia: string, elapsedMs: number) {
  if (!session || session.entry === 'link' || session.reachedDestination) {
    return;
  }
  session.reachedDestination = true;
  trackSettings('Settings Destination Reached', {
    page,
    landingPage: session.landingPage,
    pageIndex: session.pageCount,
    entry: session.entry,
    arrivedVia,
    flattened: isFlattenedSettingsEnabled(),
    elapsedMs,
    metricValue: elapsedMs,
  });
}

function viewPage(page: string, pageKey: string) {
  const now = performance.now();
  const flattened = isFlattenedSettingsEnabled();

  if (!session) {
    const entry = takeEntry(now);
    session = {
      id: nextSessionId++,
      startedAt: now,
      entry,
      landingPage: page,
      currentPage: page,
      currentPageKey: pageKey,
      pageCount: 0,
      reachedDestination: false,
    };
    setSettingsSessionId(session.id);
    trackSettings('Settings Session Started', { entry, landingPage: page, flattened });
  }

  setSettingsSessionId(session.id);

  if (session.pageCount > 0 && session.currentPageKey === pageKey) {
    return;
  }

  const elapsedMs = Math.round(now - session.startedAt);
  const isLanding = session.pageCount === 0;
  const arrivedVia = isLanding ? session.entry : takeNavigation(now);
  session.pageCount += 1;
  session.currentPage = page;
  session.currentPageKey = pageKey;

  trackSettings('Settings Page Viewed', {
    page,
    scope: settingsScope(page),
    pageIndex: session.pageCount,
    isLanding,
    entry: session.entry,
    arrivedVia,
    flattened,
    elapsedMs,
    metricValue: elapsedMs,
  });

  if (!isLanding) {
    reachDestination(page, arrivedVia, elapsedMs);
  }
}

export function getSettingsSessionId() {
  return session?.id;
}

export function markSettingsTaskCompleted(task: SettingsTask, sessionId: number | undefined) {
  const flattened = isFlattenedSettingsEnabled();
  if (!session || session.id !== sessionId) {
    trackSettings('Settings Task Completed', { task, inSession: false, flattened });
    return;
  }
  const elapsedMs = Math.round(performance.now() - session.startedAt);
  trackSettings('Settings Task Completed', {
    task,
    inSession: true,
    page: session.currentPage,
    pageCount: session.pageCount,
    entry: session.entry,
    flattened,
    elapsedMs,
    metricValue: elapsedMs,
  });
  reachDestination(session.currentPage, 'task', elapsedMs);
}

export function resetSettingsSessionForTests() {
  session = undefined;
  pendingEntry = undefined;
  pendingNavigation = undefined;
  clearSettingsSessionId();
}

export function useTrackSettingsSession(pathPattern: string | undefined) {
  const location = useLocation();
  const { pathname } = location;
  const drawerProjectKey = new URLSearchParams(location.search).get(PROJECT_SETTINGS_DRAWER_PARAM);

  useEffect(() => {
    if (!pathPattern) {
      clearSettingsSessionId();
      return;
    }
    if (isSettingsPathPattern(pathPattern)) {
      const page = drawerProjectKey !== null ? `${pathPattern}${DRAWER_PAGE_SUFFIX}` : pathPattern;
      viewPage(page, `${pathPattern} ${pathname} ${drawerProjectKey ?? ''}`);
    } else {
      endSession(pathPattern);
    }
  }, [pathPattern, pathname, drawerProjectKey]);

  useEffect(() => {
    const onPageHide = () => endSession('unload');
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);
}
